"use client";

import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { doc, getDoc, collection, addDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../../firebase";
import { PaymentFlow } from "./PaymentFlow";
import {
  FileText,
  ArrowLeft,
  CheckCircle,
  Clock,
  AlertCircle,
} from "lucide-react";

interface InvoiceLineItem {
  id: string;
  description: string;
  quantity: number;
  unitPrice: number;
}

interface InvoiceData {
  ownerWallet: string;
  ownerUsername: string;
  ownerLogoURL: string | null;
  recipientEmail: string;
  amount: number;
  currency: string;
  invoiceNumber: string;
  dueDate: string;
  status: "draft" | "sent" | "paid";
  title: string;
  description: string;
  lineItems: InvoiceLineItem[];
  brandColor?: string;
  discountAmount?: number;
  terms?: string;
  senderAddress?: string;
}

const formatDate = (dateStr: string) => {
  if (!dateStr) return "";
  try {
    return new Date(dateStr + "T00:00:00").toLocaleDateString("en-US", {
      month: "long",
      day: "numeric",
      year: "numeric",
    });
  } catch {
    return dateStr;
  }
};

const StatusBadge = ({ status }: { status: string }) => {
  const styles: Record<string, string> = {
    draft: "bg-gray-100 text-gray-600",
    sent: "bg-amber-100 text-amber-700",
    paid: "bg-green-100 text-green-700",
  };
  return (
    <span
      className={`inline-block text-xs px-3 py-1 rounded-full font-medium ${styles[status] || styles.draft}`}
    >
      {status.charAt(0).toUpperCase() + status.slice(1)}
    </span>
  );
};

export default function PublicInvoicePage() {
  const { invoiceId } = useParams<{ invoiceId: string }>();
  const [invoice, setInvoice] = useState<InvoiceData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [theme, setTheme] = useState<"dark" | "light">("dark");
  const [invoiceDisabledMethods, setInvoiceDisabledMethods] = useState<string[]>([]);

  useEffect(() => {
    const fetchInvoice = async () => {
      if (!invoiceId) {
        setError("Invalid invoice link.");
        setLoading(false);
        return;
      }

      try {
        const snap = await getDoc(doc(db, "invoices", invoiceId));
        if (!snap.exists()) {
          setError("This invoice does not exist or has been removed.");
          setLoading(false);
          return;
        }
        const invoiceData = snap.data() as InvoiceData;
        setInvoice(invoiceData);

        // Fetch owner's theme preference
        if (invoiceData.ownerWallet) {
          try {
            const ownerWalletLower = invoiceData.ownerWallet.toLowerCase();
            const userSnap = await getDoc(doc(db, "users", ownerWalletLower));
            if (userSnap.exists()) {
              const userData = userSnap.data();
              if (userData.collectSettings?.paymentTheme) {
                setTheme(userData.collectSettings.paymentTheme);
              }
              if (userData.disabledMethodsByType?.invoice) {
                setInvoiceDisabledMethods(userData.disabledMethodsByType.invoice);
              }
            }
          } catch {
            // Theme fetch is non-critical, keep default dark
          }

          // Log page view
          addDoc(collection(db, "users", invoiceData.ownerWallet.toLowerCase(), "pageViews"), {
            type: "invoice",
            timestamp: serverTimestamp(),
          }).catch(() => {});
        }
      } catch (err) {
        console.error("Error fetching invoice:", err);
        setError("Failed to load invoice. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    fetchInvoice();
  }, [invoiceId]);

  if (loading) {
    return <div className="min-h-screen bg-gradient-to-br from-[#090D14] via-[#0A0E15] to-[#0B0F16]" />;
  }

  if (error || !invoice) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#090D14] via-[#0A0E15] to-[#0B0F16] p-4">
        <div className="bg-gradient-to-br from-[#0F1419] to-[#1A1F2B] border border-gray-700/50 rounded-3xl p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-red-500/10 flex items-center justify-center">
            <AlertCircle className="w-8 h-8 text-red-400" />
          </div>
          <h2 className="text-xl font-bold text-white mb-3">
            Invoice Not Found
          </h2>
          <p className="text-gray-400 mb-6">
            {error || "This invoice link is invalid."}
          </p>
          <Link
            to="/login"
            className="inline-flex items-center gap-2 text-[#61CE70] hover:text-[#72D981] font-medium transition-colors"
          >
            <ArrowLeft size={18} />
            Go to CashTap
          </Link>
        </div>
      </div>
    );
  }

  const isLight = theme === "light";
  const brandColor = invoice.brandColor || "#6366f1";
  const lineItemsSubtotal = invoice.lineItems && invoice.lineItems.length > 0
    ? invoice.lineItems.reduce((sum, item) => sum + item.quantity * item.unitPrice, 0)
    : invoice.amount;
  return (
    <div className={`min-h-screen ${isLight ? "bg-gray-50" : "bg-gradient-to-br from-[#090D14] via-[#0A0E15] to-[#0B0F16]"}`}>
      {/* Header */}
      <header className={`w-full border-b ${isLight ? "border-gray-200 bg-white/80" : "border-gray-800/50 bg-[#090D14]/80"} backdrop-blur-lg`}>
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-center">
          <Link to="/" className={`flex items-center gap-2.5 text-base ${isLight ? "text-gray-500" : "text-gray-400"}`}>
            <span className="font-medium">Powered by</span>
            <img src="/logo.png" alt="CashTap" className="h-9 w-auto" />
          </Link>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-2xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        {/* Page Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full mb-4" style={{ backgroundColor: `${brandColor}15`, borderColor: `${brandColor}30`, borderWidth: 1 }}>
            <FileText className="w-8 h-8" style={{ color: brandColor }} />
          </div>
          <h1 className={`text-2xl sm:text-3xl font-bold ${isLight ? "text-gray-900" : "text-white"} mb-2`}>
            Invoice from {invoice.ownerUsername || "Sender"}
          </h1>
          <p className={`${isLight ? "text-gray-500" : "text-gray-400"} text-sm`}>
            Invoice #{invoice.invoiceNumber}
          </p>
        </div>

        {/* Invoice Card */}
        <div className="rounded-3xl overflow-hidden shadow-2xl mb-8">
          {/* Accent bar */}
          <div className="h-2" style={{ backgroundColor: brandColor }} />
          <div className="bg-white p-6 sm:p-8 text-gray-900">
            {/* Header: logo + invoice details */}
            <div className="flex justify-between items-start mb-8">
              <div>
                {invoice.ownerLogoURL ? (
                  <img
                    src={invoice.ownerLogoURL}
                    alt="Logo"
                    className="h-12 object-contain mb-3"
                  />
                ) : (
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-3" style={{ backgroundColor: `${brandColor}15` }}>
                    <FileText size={24} style={{ color: brandColor }} />
                  </div>
                )}
                <h2 className="text-xl font-bold text-gray-900">
                  {invoice.title || "Invoice"}
                </h2>
                <p className="text-gray-500 text-sm">
                  #{invoice.invoiceNumber}
                </p>
              </div>
              <div className="text-right space-y-2">
                {invoice.dueDate && (
                  <div>
                    <p className="text-xs text-gray-400">Due Date</p>
                    <div className="flex items-center gap-1.5 justify-end">
                      <Clock size={14} className="text-gray-400" />
                      <p className="font-semibold text-gray-700 text-sm">
                        {formatDate(invoice.dueDate)}
                      </p>
                    </div>
                  </div>
                )}
                <StatusBadge status={invoice.status} />
              </div>
            </div>

            {/* From / To */}
            <div className="grid grid-cols-2 gap-8 mb-8 pb-6 border-b border-gray-200">
              <div>
                <p className="text-xs text-gray-400 uppercase font-semibold tracking-wide mb-1">
                  From
                </p>
                <p className="font-medium text-gray-800">
                  {invoice.ownerUsername || "Sender"}
                </p>
                {invoice.senderAddress && (
                  <p className="text-xs text-gray-500 mt-1 whitespace-pre-wrap">
                    {invoice.senderAddress}
                  </p>
                )}
              </div>
              <div>
                <p className="text-xs text-gray-400 uppercase font-semibold tracking-wide mb-1">
                  Bill To
                </p>
                <p className="font-medium text-gray-800">
                  {invoice.recipientEmail || "Recipient"}
                </p>
              </div>
            </div>

            {/* Line Items Table */}
            {invoice.lineItems && invoice.lineItems.length > 0 ? (
              <div className="mb-6">
                <table className="w-full">
                  <thead>
                    <tr className="border-b-2" style={{ borderColor: `${brandColor}30` }}>
                      <th className="text-left py-3 text-xs text-gray-500 uppercase font-semibold tracking-wide">
                        Description
                      </th>
                      <th className="text-right py-3 text-xs text-gray-500 uppercase font-semibold tracking-wide">
                        Qty
                      </th>
                      <th className="text-right py-3 text-xs text-gray-500 uppercase font-semibold tracking-wide">
                        Price
                      </th>
                      <th className="text-right py-3 text-xs text-gray-500 uppercase font-semibold tracking-wide">
                        Total
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {invoice.lineItems.map((item, idx) => (
                      <tr
                        key={idx}
                        className="border-b border-gray-100"
                      >
                        <td className="py-3 text-sm text-gray-700">
                          {item.description || "Item"}
                        </td>
                        <td className="py-3 text-sm text-gray-600 text-right">
                          {item.quantity}
                        </td>
                        <td className="py-3 text-sm text-gray-600 text-right">
                          ${item.unitPrice.toFixed(2)}
                        </td>
                        <td className="py-3 text-sm font-medium text-gray-800 text-right">
                          ${(item.quantity * item.unitPrice).toFixed(2)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {/* Subtotal / Tax / Discount / Total */}
                <div className="mt-4 pt-4 border-t border-gray-200 space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Subtotal</span>
                    <span className="text-gray-700 font-medium">${lineItemsSubtotal.toFixed(2)}</span>
                  </div>
                  {(invoice.discountAmount || 0) > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500">Discount</span>
                      <span className="text-green-600 font-medium">-${(invoice.discountAmount || 0).toFixed(2)}</span>
                    </div>
                  )}
                  <div className="flex justify-between items-center pt-3 border-t-2" style={{ borderColor: `${brandColor}30` }}>
                    <span className="text-lg font-bold text-gray-700">Total Due</span>
                    <span className="text-2xl font-bold" style={{ color: brandColor }}>
                      ${invoice.amount.toFixed(2)} USD
                    </span>
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center py-6 mb-6 rounded-2xl" style={{ backgroundColor: `${brandColor}08` }}>
                <p className="text-3xl font-bold" style={{ color: brandColor }}>
                  ${invoice.amount.toFixed(2)}
                </p>
                <p className="text-gray-500 text-sm mt-1">USD</p>
              </div>
            )}

            {/* Description / Notes */}
            {invoice.description && (
              <div className="bg-gray-50 rounded-xl p-4 mb-3">
                <p className="text-xs text-gray-400 uppercase font-semibold tracking-wide mb-1">
                  Notes
                </p>
                <p className="text-sm text-gray-700 whitespace-pre-wrap">
                  {invoice.description}
                </p>
              </div>
            )}

            {/* Terms */}
            {invoice.terms && (
              <div className="bg-gray-50 rounded-xl p-4">
                <p className="text-xs text-gray-400 uppercase font-semibold tracking-wide mb-1">
                  Terms
                </p>
                <p className="text-sm text-gray-600 whitespace-pre-wrap">
                  {invoice.terms}
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Payment Section */}
        {invoice.status !== "paid" ? (
          <PaymentFlow
            targetWalletAddress={invoice.ownerWallet}
            recipientName={invoice.ownerUsername}
            isPublic={true}
            showWalletDisplay={false}
            showManagePayments={false}
            fixedAmount={invoice.amount}
            title={`Pay Invoice #${invoice.invoiceNumber}`}
            subtitle={`Amount due: $${invoice.amount.toFixed(2)} USD`}
            buttonText="Pay Invoice"
            accentColor={invoice.brandColor || "#6366f1"}
            theme={theme}
            disabledPaymentMethods={invoiceDisabledMethods}
            invoiceSource="manual"
            invoiceCollectSlug={invoiceId}
          />
        ) : (
          <div className={`text-center p-8 ${isLight ? "bg-green-50 border-green-200" : "bg-green-500/10 border-green-500/30"} rounded-3xl border`}>
            <CheckCircle className={`w-12 h-12 ${isLight ? "text-green-500" : "text-green-400"} mx-auto mb-3`} />
            <h3 className={`text-xl font-bold ${isLight ? "text-gray-900" : "text-white"} mb-1`}>
              Invoice Paid
            </h3>
            <p className={`${isLight ? "text-gray-500" : "text-gray-400"} text-sm`}>
              This invoice has been paid in full. Thank you!
            </p>
          </div>
        )}

        {/* Footer */}
        <div className="mt-12 text-center space-y-3">
          <div className="flex items-center justify-center gap-2">
            <img
              src="/cashtap-logo.png"
              alt="CashTap"
              className="h-5 w-auto"
              onError={(e) => {
                (e.target as HTMLImageElement).style.display = "none";
              }}
            />
            <span className={`${isLight ? "text-gray-400" : "text-gray-500"} text-xs`}>Secure payments powered by CashTap</span>
          </div>
        </div>
      </main>
    </div>
  );
}
